import React from 'react'
import { useTheme } from '@mui/material/styles'
import { useTranslation } from 'react-i18next'
import { useAppLocale } from '../../i18n/localeUtils'
import { formatDateYYYYMMDD } from '../../packages/utils/numberFormatters'
import * as datefns from 'date-fns'
import { IssueEntity } from 'types/IssueEntity'
import { isValidSourceType } from '../../utils/issues/sourceTypes'
import { SourceType } from 'types/SourceType'
import Stack from '@mui/system/Stack'
import Typography from '@mui/material/Typography'

function hasSourceOfType(issue: IssueEntity, type: SourceType) {
    if (!isValidSourceType(type)) {
        throw new Error(`Invalid source type: ${type}`)
    }
    return issue.sources.some((source) => source.sourceType === type)
}

function useStatusMessage(issue: IssueEntity) {
    const theme = useTheme()
    const { t } = useTranslation()
    if (!issue) return null

    const datePart = (date: string | null | Date) => (date ? datefns.startOfDay(new Date(date)) : null)

    const today = datePart(new Date()) as Date

    const {
        type,
        statusStage,
        statusComplete,
        noTermsInitially,
        rightsEndDate,
        unitIsTrading,
        subEndDate,
        subStartDate,
    } = issue

    const rightsEndDateFormatted = datePart(rightsEndDate)
    const subEndDateFormatted = datePart(subEndDate)
    const subStartDateFormatted = datePart(subStartDate)

    if (hasSourceOfType(issue, 'Cancellation')) {
        return { color: theme.palette.entityStatus.cancelled, text: t('pages.issues.publicStatus.cancelled') }
    }

    if (type === 'Rights issue') {
        if (noTermsInitially && !hasSourceOfType(issue, 'Terms')) {
            return {
                color: theme.palette.entityStatus.incomplete,
                text: t('pages.issues.publicStatus.upcomingMissingTerms'),
            }
        }

        if (hasSourceOfType(issue, 'Outcome') && !unitIsTrading) {
            return { color: theme.palette.entityStatus.complete, text: t('pages.issues.publicStatus.completed') }
        }

        if (subEndDateFormatted && datefns.isAfter(today, subEndDateFormatted)) {
            if (unitIsTrading) {
                return { color: theme.palette.entityStatus.complete, text: t('pages.issues.publicStatus.btaBtuTrading') }
            }
            return {
                color: theme.palette.entityStatus.complete,
                text: t('pages.issues.publicStatus.subscriptionPeriodEnded'),
            }
        }

        if (
            rightsEndDateFormatted &&
            subStartDateFormatted &&
            (datefns.isAfter(today, subStartDateFormatted) || datefns.isSameDay(today, subStartDateFormatted))
        ) {
            return {
                color: theme.palette.entityStatus.ongoing,
                text: t('pages.issues.publicStatus.subscriptionPeriodOngoing'),
            }
        }

        return { color: theme.palette.entityStatus.incomplete, text: t('pages.issues.publicStatus.upcoming') }
    }

    if (['Directed issue', 'Non-cash issue', 'ABB Directed issue'].includes(type)) {
        if (statusStage === 10 && !!statusComplete) {
            return { color: theme.palette.entityStatus.complete, text: t('pages.issues.publicStatus.completed') }
        }

        if (type === 'ABB Directed issue') {
            return { color: theme.palette.entityStatus.ongoing, text: t('pages.issues.publicStatus.ongoing') }
        }

        return { color: theme.palette.entityStatus.incomplete, text: t('pages.issues.publicStatus.announced') }
    }

    if (type === 'IPO') {
        if (subStartDateFormatted && datefns.isBefore(today, subStartDateFormatted)) {
            return { color: theme.palette.entityStatus.incomplete, text: t('pages.issues.publicStatus.announced') }
        }

        if (
            subEndDateFormatted &&
            subStartDateFormatted &&
            (datefns.isAfter(today, subStartDateFormatted) || datefns.isSameDay(today, subStartDateFormatted)) &&
            (datefns.isBefore(today, subEndDateFormatted) || datefns.isSameDay(today, subEndDateFormatted))
        ) {
            return {
                color: theme.palette.entityStatus.ongoing,
                text: t('pages.issues.publicStatus.subscriptionPeriodOngoing'),
            }
        }

        if (
            subEndDateFormatted &&
            (datefns.isAfter(today, subEndDateFormatted) || datefns.isSameDay(today, subEndDateFormatted)) &&
            issue.unitSubTotal === null &&
            issue.secondarySub === null
        ) {
            return {
                color: theme.palette.entityStatus.complete,
                text: t('pages.issues.publicStatus.subscriptionPeriodEnded'),
            }
        }

        return { color: theme.palette.entityStatus.complete, text: t('pages.issues.publicStatus.completed') }
    }

    return null
}

export function statusMessageWithExtraInfo(
    issue: IssueEntity,
    t: (key: string, options?: Record<string, unknown>) => string,
    locale: string,
) {
    if (!issue) return null

    const datePart = (date: string | null | Date) => (date ? datefns.startOfDay(new Date(date)) : null)

    const today = datePart(new Date()) as Date

    const {
        type,
        statusStage,
        statusComplete,
        noTermsInitially,
        incRightsDate,
        rightsEndDate,
        unitIsTrading,
        subEndDate,
        subStartDate,
        publicationDate,
        generalMeetingDate,
    } = issue

    const incRightsDateFormatted = datePart(incRightsDate)
    const rightsEndDateFormatted = datePart(rightsEndDate)
    const subEndDateFormatted = datePart(subEndDate)
    const subStartDateFormatted = datePart(subStartDate)
    const publicationDateFormatted = datePart(publicationDate)
    const generalMeetingDateFormatted = datePart(generalMeetingDate)

    if (hasSourceOfType(issue, 'Cancellation')) {
        return t('pages.issues.publicStatus.cancelled')
    }

    if (type === 'Rights issue') {
        if (noTermsInitially && !hasSourceOfType(issue, 'Terms')) {
            return t('pages.issues.publicStatus.upcomingMissingTermsLong')
        }

        if (hasSourceOfType(issue, 'Outcome') && !unitIsTrading) {
            return t('pages.issues.publicStatus.completed')
        }

        if (subEndDateFormatted && datefns.isAfter(today, subEndDateFormatted)) {
            let str = t('pages.issues.publicStatus.subscriptionPeriodEnded')
            if (hasSourceOfType(issue, 'Outcome')) {
                str = t('pages.issues.publicStatus.subscriptionPeriodEndedWithOutcome')
            }
            if (unitIsTrading) {
                str += t('pages.issues.publicStatus.subscriptionPeriodEndedWithBtaBtu')
            }
            return str
        }

        if (
            rightsEndDateFormatted &&
            subStartDateFormatted &&
            (datefns.isAfter(today, subStartDateFormatted) || datefns.isSameDay(today, subStartDateFormatted))
        ) {
            return t('pages.issues.publicStatus.subscriptionPeriodOngoingWithRightsEnd', {
                date: formatDateYYYYMMDD(rightsEndDateFormatted, { locale }),
            })
        }

        if (incRightsDateFormatted && datefns.isAfter(today, incRightsDateFormatted)) {
            return t('pages.issues.publicStatus.publishedRightsDeadlinePassed', {
                date: formatDateYYYYMMDD(incRightsDateFormatted, { locale }),
            })
        }

        if (
            incRightsDateFormatted &&
            (datefns.isBefore(today, incRightsDateFormatted) || datefns.isSameDay(today, incRightsDateFormatted))
        ) {
            return t('pages.issues.publicStatus.upcomingRightsDeadline', {
                date: formatDateYYYYMMDD(incRightsDateFormatted, { locale }),
            })
        }

        return t('pages.issues.publicStatus.upcomingMissingDates')
    }

    if (['Directed issue', 'Non-cash issue', 'ABB Directed issue'].includes(type)) {
        if (statusStage === 10 && !!statusComplete) {
            return t('pages.issues.publicStatus.completedOnDate', {
                date: formatDateYYYYMMDD(publicationDateFormatted, { locale }),
            })
        }

        if (type === 'ABB Directed issue') {
            return t('pages.issues.publicStatus.ongoing')
        }

        if (generalMeetingDateFormatted && datefns.isBefore(today, generalMeetingDateFormatted)) {
            return t('pages.issues.publicStatus.announcedWithMeeting', {
                date: formatDateYYYYMMDD(generalMeetingDateFormatted, { locale }),
            })
        }

        return t('pages.issues.publicStatus.announcedIssue')
    }

    if (type === 'IPO') {
        if (subStartDateFormatted && datefns.isBefore(today, subStartDateFormatted)) {
            return t('pages.issues.publicStatus.announced')
        }

        if (
            subEndDateFormatted &&
            subStartDateFormatted &&
            (datefns.isAfter(today, subStartDateFormatted) || datefns.isSameDay(today, subStartDateFormatted)) &&
            (datefns.isBefore(today, subEndDateFormatted) || datefns.isSameDay(today, subEndDateFormatted))
        ) {
            return t('pages.issues.publicStatus.subscriptionPeriodOngoingShort')
        }

        if (
            subEndDateFormatted &&
            (datefns.isAfter(today, subEndDateFormatted) || datefns.isSameDay(today, subEndDateFormatted)) &&
            issue.unitSubTotal === null &&
            issue.secondarySub === null
        ) {
            return t('pages.issues.publicStatus.subscriptionPeriodEnded')
        }

        return t('pages.issues.publicStatus.completed')
    }

    return null
}

export const IssuePublicStatusWithExtraInfo = ({ issue }: { issue: IssueEntity }) => {
    const { t } = useTranslation()
    const { intlDateLocale: locale } = useAppLocale()
    const message = statusMessageWithExtraInfo(issue, t, locale)
    return message ? (
        <>
            {t('pages.issues.publicStatus.statusLabel')} {message}
        </>
    ) : null
}

export const IssuePublicStatus = ({ issue }: { issue: IssueEntity }) => {
    const status = useStatusMessage(issue)

    if (!status) return null

    return (
        <Stack
            direction='row'
            spacing={1}
            sx={{
                alignItems: 'center',
            }}
        >
            <div
                style={{
                    height: '10px',
                    width: '10px',
                    margin: 0,
                    backgroundColor: status.color,
                    borderRadius: '50%',
                }}
            />
            <Typography>{status.text}</Typography>
        </Stack>
    )
}
